/**
 * Date utility functions for consistent date handling across the app.
 * 
 * IMPORTANT: Always use parseLocalDate when working with date strings from the database
 * to avoid timezone issues. new Date('2026-01-01') parses as UTC midnight which can
 * shift back a day in local time.
 *
 * Example (in some timezones):
 *   // ❌ Avoid: off-by-one-day when treating YYYY-MM-DD as a local date
 *   const bad = new Date('2026-01-01');        // Parsed as 2026-01-01T00:00:00Z
 *   console.log(bad.toString());              // May show as 2025-12-31 in local time
 *
 *   // ✅ Use parseLocalDate to get a true local date at midnight
 *   const good = parseLocalDate('2026-01-01'); // Local 2026-01-01 00:00 in the current TZ
 *   console.log(good.toString());             // Stays on 2026-01-01 locally
 */

/**
 * Parse a date string (YYYY-MM-DD) as local midnight, avoiding UTC timezone issues.
 * 
 * Note: The regex validates format only (\d{4}-\d{2}-\d{2}), not value ranges.
 * Invalid values like "2024-99-99" pass the regex but create an invalid Date object,
 * which is caught by the rollover check below. This two-step approach handles all
 * edge cases: invalid format, out-of-range values, and date rollover (e.g., Feb 31).
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date|null} - Date object at local midnight, or null if invalid
 */
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  // Basic YYYY-MM-DD format validation
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  // Ensure the date didn't roll over (e.g., 2024-02-31 -> 2024-03-02)
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

/**
 * Format a Date object as YYYY-MM-DD string.
 * @param {Date} date - Date object to format
 * @returns {string} - Formatted date string, or empty string if invalid
 */
export const formatLocalDate = (date) => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/**
 * Get today's date as YYYY-MM-DD string in local timezone.
 * @returns {string} - Today's date formatted as YYYY-MM-DD
 */
export const getToday = () => formatLocalDate(new Date());

/**
 * Get the year-month key (YYYY-MM) from a Date object.
 * @param {Date} date - Date object
 * @returns {string} - Year-month key in YYYY-MM format
 */
export const getMonthKey = (date) => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Check if a date is today.
 * @param {Date} date - Date object to check
 * @returns {boolean} - True if the date is today
 */
export const isToday = (date) => {
  if (!date) return false;
  const today = new Date();
  return date.getFullYear() === today.getFullYear() &&
         date.getMonth() === today.getMonth() &&
         date.getDate() === today.getDate();
};

/**
 * Check if a date is in the past (before today).
 * @param {Date} date - Date object to check
 * @returns {boolean} - True if the date is before today
 */
export const isPastDate = (date) => {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
};

/**
 * Format a MM-DD string as a human-readable month and day (e.g., "March 14").
 * @param {string} mmdd - Date string in MM-DD format (e.g., "03-14")
 * @returns {string} - Formatted string like "March 14", or empty string if invalid
 */
export const formatMonthDay = (mmdd) => {
  if (!mmdd || !/^\d{2}-\d{2}$/.test(mmdd)) return '';
  const [month, day] = mmdd.split('-').map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return `${monthNames[month - 1]} ${day}`;
};

/**
 * Convert a full date string (YYYY-MM-DD) to MM-DD format for planned date storage.
 * @param {string} dateStr - Full date string in YYYY-MM-DD format
 * @returns {string} - MM-DD string (e.g., "03-14"), or empty string if invalid
 */
export const dateToMonthDay = (dateStr) => {
  if (!dateStr) return '';
  const match = dateStr.match(/^\d{4}-(\d{2}-\d{2})$/);
  return match ? match[1] : '';
};

/**
 * Convert a MM-DD string to a full YYYY-MM-DD date string using the given year.
 * Defaults to the current year.
 * @param {string} mmdd - Date string in MM-DD format (e.g., "03-14")
 * @param {number} [year] - Year to apply (defaults to current year)
 * @returns {string} - Full date string in YYYY-MM-DD format, or empty string if invalid
 */
export const monthDayToFullDate = (mmdd, year) => {
  if (!mmdd || !/^\d{2}-\d{2}$/.test(mmdd)) return '';
  const targetYear =
    typeof year === 'number' && Number.isFinite(year)
      ? year
      : new Date().getFullYear();
  const dateStr = `${targetYear}-${mmdd}`;
  // Validate the constructed date (e.g., catch non-leap-year Feb 29)
  const parsed = parseLocalDate(dateStr);
  if (!parsed) {
    // Clamp to last valid day of the month (e.g., Feb 29 → Feb 28)
    const [mm, dd] = mmdd.split('-');
    const month = parseInt(mm, 10);
    const lastDay = new Date(targetYear, month, 0).getDate();
    const clampedDay = Math.min(parseInt(dd, 10), lastDay);
    return `${targetYear}-${mm}-${String(clampedDay).padStart(2, '0')}`;
  }
  return dateStr;
};

/**
 * Get relative date description (e.g., "Today", "Yesterday", "3 days ago").
 * @param {Date|string} date - Date object or YYYY-MM-DD string
 * @returns {string} - Human-readable relative date
 */
export const getRelativeDate = (date) => {
  const parsed = typeof date === 'string' ? parseLocalDate(date) : date;
  if (!parsed) return '';
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Create a copy to avoid mutating the input date
  const compareDate = new Date(parsed);
  compareDate.setHours(0, 0, 0, 0);
  
  const diffDays = Math.round((today - compareDate) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays === -1) return 'Tomorrow';
  if (diffDays > 1 && diffDays <= 7) return `${diffDays} days ago`;
  if (diffDays < -1 && diffDays >= -7) return `In ${Math.abs(diffDays)} days`;
  
  return compareDate.toLocaleDateString();
};
